import { useGraphQLQuery } from '@betterment/graphql';
import { StepProps, useFlowState } from '@betterment/js-runtime';
import {
  Box,
  Button,
  CenteredLayout,
  Heading,
  IconButton,
  PancakeStack,
  Text,
  WorkflowHeader,
} from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';

import { OrderUnitType, SaleScope, SellOrderProps, ValidatedOrderValues, Validations } from './types';

import { WithLayout } from '../../RetailApp/components/WithLayout';
import { locationAssign } from '../../Shared/path-helpers';
import { DirectTradingAccountSellSecurityPositionsDocument } from '../graphql/directTradingAccountSellSecurityPositions.generated';
import { useOrderValidation } from '../hooks/useOrderValidation';

export const SellAllWarningStep: React.FC<SellOrderProps & StepProps> = ({
  accountId,
  securityId,
  goToNextStep,
  goToPreviousStep,
}) => {
  const { data } = useGraphQLQuery(DirectTradingAccountSellSecurityPositionsDocument, {
    variables: { accountId },
    throwOnError: true,
  });
  const { flowState } = useFlowState<ValidatedOrderValues & Validations>();

  const { validateOrder, loading: isMutationLoading } = useOrderValidation();

  if (!data) return null;

  const { account } = data;
  const securityPosition = account.securityPositions.find(sp => sp.financialSecurity.id === securityId);

  if (!securityPosition) return null;

  const { financialSecurity } = securityPosition;

  const handleContinue = async () => {
    const orderSharesAmount = securityPosition.shares;

    const validationResult = await validateOrder({
      accountId: account.id,
      securityId,
      orderSharesAmount,
      orderDollarAmountCents: undefined,
      sellAll: true,
    });

    if (validationResult) {
      const { result } = validationResult;

      if (result) {
        const nextStepData = {
          order_dollar_amount_cents: undefined,
          order_shares_amount: orderSharesAmount,
          order_unit: OrderUnitType.Shares,
          order_type: SaleScope.SellAll,
          estimated_trade_timing: result.estimatedTradeTiming,
          sale_over_90_percent: result.saleOver90Percent,
        };

        goToNextStep(nextStepData);
      }
    }
  };

  return (
    <ErrorBoundary>
      <WithLayout parts={{ sidebar: false, footer: true }}>
        <PancakeStack css={{ maxWidth: '100%', bg: 'background.surface' }}>
          <PancakeStack.Header
            css={{
              maxWidth: '100%',
              display: 'flex',
              justifyContent: 'center',
              borderBottomStyle: 'solid',
              borderBottomWidth: 'thin',
              borderColor: 'border',
            }}
          >
            <WorkflowHeader css={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <WorkflowHeader.Leading>
                <IconButton name="LeftArrowSmall" label="Back" spacing="compact" onClick={goToPreviousStep} />
              </WorkflowHeader.Leading>
              <WorkflowHeader.Title />
              <WorkflowHeader.Trailing>
                <IconButton
                  name="X"
                  label="Close"
                  spacing="compact"
                  onClick={() => locationAssign(flowState.returnToPath)}
                />
              </WorkflowHeader.Trailing>
            </WorkflowHeader>
          </PancakeStack.Header>
          <PancakeStack.Main css={{ paddingRight: 'space.200', paddingLeft: 'space.200' }}>
            <CenteredLayout size="medium" css={{ paddingTop: 'space.500', width: 'token(sizes.container.large)' }}>
              <Heading css={{ marginBottom: '24px' }}>
                This order will sell your full holding in {financialSecurity.symbol}.
              </Heading>
              <Box css={{ marginBottom: 'space.400' }}>
                <Text>
                  To help account for price changes between trade submission and execution, we will sell the full
                  position.
                </Text>
              </Box>
              <Box>
                <Text css={{ fontWeight: 'bold', marginBottom: 'space.200' }}>What to know:</Text>
                <ul>
                  <li style={{ marginBottom: '8px' }}>
                    <Text>To keep some shares, go back and edit the amount before submitting.</Text>
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <Text>The final sell amount may change slightly due to price movement.</Text>
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <Text>
                      Any dividends paid after the sale will be added to your cash balance in your self-directed
                      investing account.
                    </Text>
                  </li>
                </ul>
              </Box>
              <Button type="submit" onClick={handleContinue} loading={isMutationLoading}>
                Continue
              </Button>
            </CenteredLayout>
          </PancakeStack.Main>
        </PancakeStack>
      </WithLayout>
    </ErrorBoundary>
  );
};
